
import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { Ticket, UserTicket } from "@models/Ticket";
import { TicketType } from "@dndModels/Types";
import { TICKET_TYPES } from "@dndModels/Constants";
import { ModelBase, User } from "@models";

import API from "@dndUtilities/system_api";
// import DataUtilities from '../../utilities/common_utilities';

import LoadingBox from "@components/input/LoadingBox";
import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import ArrowBackButton from "@components/input/buttons/ArrowBackButton";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import SelectObjectIndex from "@dndComponents/select/SelectObjectIndex";

const api = API.getInstance();
// const common_util = DataUtilities.getInstance();

export default function UserTicketEdit() {
  const { id } = useParams();
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<UserTicket>(new UserTicket());
  const [user, setUser] = useState<User | null>(null);
  const [ticket, setTicket] = useState<Ticket>(new Ticket());
  const [ping, setPing] = useState(false);
  const [ticket_view, setTicketView] = useState("Create Ticket");
  const [ticket_filter, setTicketFilter] = useState({});
  
  const load = useCallback(async () => {
    if (id && id !== "") {
      setLoading(true);
      const res = await api.getPlayerObject({ data_type: "user_ticket", id });
      if (res instanceof UserTicket) {
        const new_obj = res;
        setObj(new_obj);
        const res2 = await api.getUsers({ "_id*ObjectIDs": [new_obj.owner_id] });
        if (res2.length === 1 && res2[0] instanceof User) {
          setUser(res2[0]);
        }
        // debugger;
        const res3 = await api.getObjects({ data_type: "ticket", filter: { user_ticket_ids: new_obj._id } });
        if (res3.length === 1 && res3[0] instanceof Ticket) {
          setTicket(res3[0]);
        }
      }
      setLoading(false);
    }
  }, [id]);

  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      load();
    }
  }, [load, fetched]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const submit = async () => {
    if (api.user && 
      (api.user.admin || 
        obj._id === "" || 
        obj.owner_id === api.user._id)) {
      setProcessing(true);
      if (obj.owner_id === "") {
        obj.owner_id = api.user._id;
        obj.owner = api.user;
      }
      const res = await api.upsertObject("user_ticket", obj);
      if (res && res.body.id) {
        obj._id = res.body.id;
      } 
      setProcessing(false);
      setNavigateTo("/dandd/user_ticket");
    }
  }

  const unassociate = async () => {
    if (ticket._id !== "") {
      setProcessing(true);
      ticket.user_ticket_ids = ticket.user_ticket_ids.filter(o => o !== obj._id);
      await api.upsertObject("ticket", ticket);
      setTicket(new Ticket());
      setProcessing(false);
    }
  }

  const createTicket = async () => {
    if (ticket._id === "") {
      setProcessing(true);
      ticket.ticket_type = obj.ticket_type;
      ticket.name = obj.subject;
      ticket.description = obj.text;
      ticket.user_ticket_ids = [obj._id];
      ticket.user_tickets = [obj];
      const res = await api.upsertObject("ticket", ticket);
      if (res && res.body.id) {
        ticket._id = res.body.id;
      } 
      setProcessing(false);
    }
  }

  const assignTicket = async (t: ModelBase) => {
    if (t instanceof Ticket) {
      setProcessing(true);
      t.user_ticket_ids.push(obj._id);
      await api.upsertObject("ticket", t);
      setTicket(t);
      setProcessing(false);
    }
  }

  const updateTicketFilter = (changed: any) => {
    console.log(changed);
    setTicketFilter(changed);
  }

  const renderUserInfo = () => {
    if (user) {
      return (
        <Grid
          size={{
            xs: 12
          }}>From User: { user.profile_name }
        </Grid>
      );
    }
    return null;
  }

  const renderTicketControl = () => {
    return (
      <>
        { ticket._id !== "" ?
          <>
            <Grid
              size={{
                xs: 9
              }}>
              <ButtonBox
                name={`Go to Associated Ticket (${ ticket.ticket_type }: ${ ticket.name })`} 
                disabled={processing}
                onClick={() => { setNavigateTo(`/dandd/ticket/edit/${ticket._id}`); }}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <ButtonBox
                name={`Remove Association`} 
                disabled={processing}
                onClick={unassociate}
              />
            </Grid>
          </>
        :
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <CenteredMenu
                options={["Create Ticket", "Associate with Existing"]}
                selected={ticket_view}
                onSelect={setTicketView}
              />
            </Grid>
            { ticket_view === "Create Ticket" ?
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <ButtonBox
                    name="Create New Ticket"
                    disabled={processing}
                    onClick={createTicket}
                  />
                </Grid>
              </>
            :
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <SelectObjectIndex
                    data_type="ticket"
                    filter={ticket_filter}
                    updateFilter={updateTicketFilter}
                    onClick={assignTicket}
                  />
                </Grid>
              </>
            }
          </>
        }
      </>
    );
  }

  if (!fetched || loading) {
    return (<div className="centered"><LoadingBox size={window.innerHeight * 0.5} height={window.innerHeight - 81} /></div>);
  } else if (api.user === null || !api.user.admin) {
    return <Navigate to="/dandd" />;
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else { 
    return (
      <Grid container spacing={0} direction="row" className="padded">
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          <ArrowBackButton 
            onClick={() => { setNavigateTo("/dandd/user_ticket"); }}
          />
          &nbsp;
          User Ticket
        </Grid>
        <Grid
          style={{ height: "calc(100vh - 150px)", overflowY: "auto" }}
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <SelectStringBox 
                name="Type"
                value={obj.ticket_type} 
                options={TICKET_TYPES}
                disabled={disabled}
                onChange={(changed: TicketType) => {
                  const new_obj = obj;
                  new_obj.ticket_type = changed;
                  setObj(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            { renderUserInfo() }
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox 
                name="Subject"
                disabled={disabled}
                value={obj.subject}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.subject = changed;
                  setObj(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox 
                name=""
                value={obj.text} 
                disabled={disabled}
                multiline
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.text = changed;
                  setObj(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            { renderTicketControl() }
          </Grid>
        </Grid>
        { !disabled &&
          <Grid
            size={{
              xs: 12
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={processing}
              onClick={submit}>
              Submit
            </Button>
            <Button
              variant="contained"
              disabled={processing}
              style={{ marginLeft: "4px" }}
              onClick={ () => { 
                setNavigateTo(`/dandd/user_ticket`);
              }}>
              Cancel
            </Button>
          </Grid>
        }
      </Grid>
    ); 
  }
}
